import "./AuthRestoreForm.css";

import {KeyboardEventHandler, useEffect, useState} from "react";

import FormHeader from "../../controls/formHeader/FormHeader";

import {Modal} from "@consta/uikit/Modal";
import {ProgressStepBar} from "@consta/uikit/ProgressStepBar";
import {Informer} from "@consta/uikit/Informer";

import StepButtons from "../../controls/stepButtons/StepButtons";

import PasswordPage from "./PasswordPage";
import EmailPage from "./EmailPage";
import {TextFieldPropValue} from "@consta/uikit/TextField";
import CodePage from "./CodePage";
import {
    confirmRestore,
    restore,
} from "../../../services/api/auth/auth";
import {errorMessageUnknown} from "../../../services/utils/network/errors";
import EmptySpace, {VSpace} from "../../controls/emptySpace/EmptySpace";
import IStorage from "../../../services/storage/IStorage";
import IAuthService from "../../../services/auth/IAuthService";

function AuthRestoreForm(props: IAuthRestoreFormProps) {
    const clean = () => {
        setPassword("");
        setEmail("");
        setActiveStepIndex(0)
        setIsNextActive(false)
        setConfirmationCode(null)
        setConfirmationUuid(null)
        resetError();
    };

    const resetError = () => {
        setIsErrorVisible(false)
        setErrorMessage("")
    } 

    const close = () => {
        clean();
        props.onClose();
    };

    const CodeLength = 6;

    const PasswordStepIndex = 0;
    const EmailStepIndex = 1;
    const CodeStepIndex = 2;

    const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
    const goNext = () => {
        if (activeStepIndex === EmailStepIndex) {
            makeRestore((success) => {
                if (success) {
                    setActiveStepIndex(activeStepIndex + 1);
                }
            });
        } else if (activeStepIndex === CodeStepIndex) {
            makeConfirmRestore();
        } else {
            setActiveStepIndex(activeStepIndex + 1);
        }
    };
    const goPrev = () => {
        setActiveStepIndex(activeStepIndex - 1);
    };

    const tryGoNext = () => {
        if (isNextActive) {
            goNext();
        }
    };

    const makeRestore = (onFinish: BoolCompletion) => {
        resetError()
        setConfirmationUuid(null);
        if (email && password) {
            setIsNextActive(false);
            restore(email, password, (response) => {
                setIsNextActive(true);
                if (response.success) {
                    if (response.confirmation_uuid) {
                        setConfirmationUuid(response.confirmation_uuid);
                        onFinish(true);
                    }
                } else {
                    setErrorMessage(response.error?.message || errorMessageUnknown)
                    setIsErrorVisible(true)
                    onFinish(false);
                }
            });
        }
    };

    const [password, setPassword] = useState<string | null>(null);
    const handlePassword = (value: string | null) => {
        setPassword(value);
    };

    const [email, setEmail] = useState<string | null>(null);
    const handleEmail = (value: TextFieldPropValue) => {
        setEmail(value);
    };

    const [confirmationUuid, setConfirmationUuid] = useState<string | null>(
        null
    );

    const [confirmationCode, setConfirmationCode] = useState<string | null>(
        null
    );
    const handleConfirmationCode = (value: TextFieldPropValue) => {
        setConfirmationCode(value);
    };

    const [isNextActive, setIsNextActive] = useState<boolean>(false);
    useEffect(() => {
        let value: string | null = null;
        if (activeStepIndex === PasswordStepIndex) {
            value = password;
        }
        if (activeStepIndex === EmailStepIndex) {
            value = email;
        }
        if (activeStepIndex === CodeStepIndex) {
            if (confirmationCode && confirmationCode.length === CodeLength) {
                value = confirmationCode;
            }
        }
        if (value) {
            setIsNextActive(true);
        } else {
            setIsNextActive(false);
        }
    }, [password, email, confirmationCode, activeStepIndex]);

    useEffect(() => {
        resetError()
    }, [password, email, confirmationCode]);

    const [isErrorVisible, setIsErrorVisible] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    type Item = {
        label: string;
        point?: number;
        progress?: boolean;
        status?: "normal" | "success" | "alert" | "warning";
        lineStatus?: "normal" | "success" | "alert" | "warning";
    };

    const steps: Item[] = [
        {
            label: "Пароль",
            point: 1,
            status: "normal",
        },
        {
            label: "E-mail",
            point: 2,
            status: "normal",
        },
        {
            label: "Подтверждение",
            point: 3,
            status: "normal",
        },
    ];

    const passwordPage = () => {
        return (
            <PasswordPage
                initialPassword={password || ""}
                onAvailablePassword={handlePassword}
            />
        );
    };

    const emailPage = () => {
        return (
            <EmailPage
                initialEmail={email || ""}
                onAvailableEmail={handleEmail}
            />
        );
    };

    const codePage = () => {
        return (
            <CodePage
                email={email || ""}
                onCodeReceived={handleConfirmationCode}
            />
        );
    };

    const errorView = () => {
        return (
            <div>
                <EmptySpace space={VSpace.Space16} />
                <Informer
                    status="alert"
                    view="filled"
                    title="Что-то пошло не так"
                    label={errorMessage}
                />
                <EmptySpace space={VSpace.Space16} />
            </div>
        );
    };

    const makeConfirmRestore = () => {
        resetError()
        if (confirmationUuid && confirmationCode) {
            setIsNextActive(false);
            confirmRestore(confirmationUuid, confirmationCode, (response) => {
                setIsNextActive(true);
                if (response.success) {
                    props.storage.setAccessToken(response.access_token)
                    props.storage.setRefreshToken(response.refresh_token)
                    close()
                    props.onFinish()
                } else {
                    setErrorMessage(response.error?.message || errorMessageUnknown)
                    setIsErrorVisible(true)
                }
            });
        }
    };

    const handleKeyboard: KeyboardEventHandler = (e) => {
        if (e.key === "Enter") tryGoNext();
    };

    return (
        <Modal
            isOpen={props.isModalOpen}
            hasOverlay
            position="top"
            onClickOutside={() => close()}
            onEsc={close}
            onKeyDown={handleKeyboard}
        >
            <div className="RegisterForm">
                <FormHeader onClose={close} text="Восстановление аккаунта" />
                <div className="RegisterForm-container">
                    <ProgressStepBar
                        className="RegisterForm-steps"
                        steps={steps}
                        activeStepIndex={activeStepIndex}
                        size="s"
                    />
                    {isErrorVisible && errorView()}
                    <div className="RegisterForm-page">
                        {activeStepIndex === PasswordStepIndex && passwordPage()}
                        {activeStepIndex === EmailStepIndex && emailPage()}
                        {activeStepIndex === CodeStepIndex && codePage()}
                    </div>
                    <StepButtons
                        stepIndex={activeStepIndex}
                        stepCount={steps.length}
                        handleNext={goNext}
                        handlePrev={goPrev}
                        isNextActive={isNextActive}
                        isPrevActive={true}
                        doneText={"Восстановить аккаунт"}
                    />
                </div>
            </div>
        </Modal>
    );
}

type IAuthRestoreFormProps = {
    isModalOpen: boolean;
    onClose: VoidFunction;
    onFinish: VoidFunction;
    storage: IStorage;
};

export default AuthRestoreForm;
